'use client'
import { useState, useEffect } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { AnimatePresence, motion } from 'framer-motion'
import { useLanguage } from '@/lib/context/LanguageContext'
import { assetUrl } from '@/lib/assetUrl'
import { useScrolled } from '@/lib/hooks/useScrolled'

const sectionKeys = ['services', 'faq']

export default function Navbar() {
  const { t, lang } = useLanguage()
  const pathname = usePathname()
  const scrolled = useScrolled(60)
  const [open, setOpen] = useState(false)
  const [activeSection, setActiveSection] = useState('')
  // What kind of section sits behind the navbar — drives logo colour + nav theme.
  // Page always opens on the dark hero, so 'dark' is the safe first-paint value.
  const [theme, setTheme] = useState<'dark' | 'light'>('dark')
  const [isMobile, setIsMobile] = useState(false)

  const isDark = theme === 'dark'

  useEffect(() => {
    let raf = 0
    const update = () => {
      raf = 0

      // Detection line 30% down the viewport — the active section is whichever
      // one crosses it. getElementById runs every tick so lazy-mounted
      // (next/dynamic) sections are picked up automatically once they exist.
      const line = window.innerHeight * 0.3
      let active = ''
      for (const key of sectionKeys) {
        const el = document.getElementById(key)
        if (!el) continue
        const r = el.getBoundingClientRect()
        if (r.top <= line && r.bottom >= line) {
          active = key
          break
        }
      }
      setActiveSection(active)

      // Probe 50px down (inside the 80px navbar): is a dark section behind it?
      const probe = 50
      let dark = false
      for (const el of document.querySelectorAll('[data-nav-dark]')) {
        const r = el.getBoundingClientRect()
        if (r.top <= probe && r.bottom >= probe) {
          dark = true
          break
        }
      }
      setTheme(dark ? 'dark' : 'light')
    }
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    update()
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [])

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const sync = () => setIsMobile(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  // Navbar renders on the blog pages too, where bare hash anchors point at
  // nothing — off the homepage, section links must navigate home first.
  // FA keeps its language via the /fa homepage.
  const faPrefix = lang === 'fa' ? '/fa' : ''
  const onHome = pathname === '/' || pathname === '/fa'
  const sectionHref = (id: string) => (onHome ? `#${id}` : `${faPrefix || '/'}#${id}`)
  const blogHref = `${faPrefix}/blog`
  const contactHref = sectionHref('contact')

  const navItems = [
    {
      key: 'services',
      label: t.nav.services,
      href: sectionHref('services'),
      isActive: activeSection === 'services',
      isPage: false,
    },
    {
      key: 'faq',
      label: t.nav.faq,
      href: sectionHref('faq'),
      isActive: activeSection === 'faq',
      isPage: false,
    },
    {
      key: 'blog',
      label: t.nav.blog,
      href: blogHref,
      isActive: pathname === blogHref || pathname.startsWith(`${blogHref}/`),
      isPage: true,
    },
  ]

  // Logo: sits inside the 80px bar on the hero, shrinks slightly once scrolled.
  // Colour follows the theme.
  const heroH = isMobile ? 30 : 44
  const smallH = isMobile ? 26 : 28

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? `md:bg-transparent md:backdrop-blur-none md:border-b-0 backdrop-blur-md border-b ${
              isDark ? 'bg-black/70 border-white/10' : 'bg-white/90 border-ink/5'
            }`
          : ''
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-4 md:px-8 h-20 flex items-center gap-6">
        {/* Logo — big on hero, shrinks on scroll; white⇄blue crossfade by theme.
            Wrapped in a flex-1 spacer so the pill (next sibling, also flex-1
            via its absolute container) stays at the geometric center of the
            navbar regardless of the logo's animated width changes. */}
        <Link
          href="/"
          aria-label="Trend"
          className="flex-1 flex justify-start hover:opacity-75 transition-opacity duration-150"
        >
          {/* Pill background wraps the logo when scrolled, masking page content
              that would otherwise show through the transparent navbar at the
              logo's vertical position. Hero state: no pill (transparent over
              video). Scrolled state: tinted pill matches the navbar theme. */}
          <div
            className={`rounded-full transition-all duration-300 ${
              scrolled
                ? `px-3 py-1 backdrop-blur-md border ${
                    isDark ? 'bg-black/30 border-white/10' : 'bg-white/80 border-ink/10'
                  }`
                : ''
            }`}
          >
            <motion.div
              className="relative block"
              initial={false}
              animate={{ height: scrolled ? smallH : heroH, y: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 30, mass: 0.8 }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element -- SVG logo; sync-assets pre-optimizes. */}
              <img
                src={assetUrl('brand/trend-logo-white.svg')}
                alt="Trend"
                width={515}
                height={199}
                // The logo is the page's LCP element (the full-screen hero is
                // excluded by Chrome's background heuristic, animated copy starts
                // at opacity 0). High priority keeps its request from queueing
                // behind the hero video on slow connections.
                fetchPriority="high"
                className="block h-full w-auto transition-opacity duration-300"
                style={{ opacity: isDark ? 1 : 0 }}
              />
              {/* eslint-disable-next-line @next/next/no-img-element -- SVG logo; sync-assets pre-optimizes. */}
              <img
                src={assetUrl('brand/trend-logo-dark.svg')}
                alt=""
                aria-hidden
                width={515}
                height={199}
                className="block h-full w-auto absolute top-0 left-0 transition-opacity duration-300"
                style={{ opacity: isDark ? 0 : 1 }}
              />
            </motion.div>
          </div>
        </Link>

        {/* Desktop pill nav — center flex item with flex-1 spacers on both
            sides (logo wrapper above, CTA wrapper below). Pill stays dead-
            centered on the navbar regardless of logo's animated width changes. */}
        <ul
          className={`hidden md:flex items-center gap-1 rounded-full border px-2 py-2 transition-all duration-300 ${
            isDark
              ? 'bg-black/30 backdrop-blur-md border-white/10'
              : 'bg-white/90 backdrop-blur-md border-ink/10'
          }`}
        >
          {navItems.map(({ key, label, href, isActive, isPage }) => {
            const pillClass = `px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
              isDark
                ? isActive
                  ? 'text-white bg-white/15'
                  : 'text-white/60 hover:text-white hover:bg-white/10'
                : isActive
                  ? 'text-ink bg-ink/8 font-semibold'
                  : 'text-muted hover:text-ink hover:bg-ink/5'
            }`
            return (
              <li key={key}>
                {isPage ? (
                  <Link href={href} className={pillClass}>
                    {label}
                  </Link>
                ) : (
                  <a href={href} className={pillClass}>
                    {label}
                  </a>
                )}
              </li>
            )
          })}
        </ul>

        {/* Right side — flex-1 spacer mirrors the logo wrapper on the left so
            the pill stays centered. Holds the desktop CTA AND the mobile menu
            button (only one shows at a time thanks to md: visibility utilities). */}
        <div className="flex-1 flex items-center justify-end gap-2">
          {/* CTA button */}
          <a
            href={contactHref}
            className={`hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
              isDark
                ? 'border border-white/20 text-white hover:border-white/40 hover:bg-white/5'
                : 'bg-primary text-white hover:opacity-90'
            }`}
          >
            {t.nav.cta}
          </a>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={() => setOpen((v) => !v)}
              className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-white hover:bg-white/10' : 'text-ink hover:bg-ink/5'}`}
              aria-label="Menu"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                {open ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu with slide animation */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="md:hidden bg-black/90 backdrop-blur-md border-t border-white/10 px-6 py-5 flex flex-col gap-4"
          >
            {navItems.map(({ key, label, href, isPage }) =>
              isPage ? (
                <Link
                  key={key}
                  href={href}
                  className="text-sm text-white/60 hover:text-white transition-colors"
                  onClick={() => setOpen(false)}
                >
                  {label}
                </Link>
              ) : (
                <a
                  key={key}
                  href={href}
                  className="text-sm text-white/60 hover:text-white transition-colors"
                  onClick={() => setOpen(false)}
                >
                  {label}
                </a>
              ),
            )}
            <a
              href={contactHref}
              className="w-fit inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white border border-white/20"
              onClick={() => setOpen(false)}
            >
              {t.nav.cta}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
